import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import type { FormContextProps } from 'src/components/form/form/types';
import { useIsModuleEnabled } from 'src/hooks/useIsModuleEnabled';

import type { AssessmentTypeEnum } from '../../../assessments/types';
import { getParentType } from './getParentType';
import TestResultFormFields from './TestResultFormFields';
import type { TestResultFormFieldsData } from './testResultSchema';
import { isCreateTestResult, TestResultFormSchema } from './testResultSchema';

export type Props = Omit<
  FormContextProps<TestResultFormFieldsData>,
  'formId' | 'i18n' | 'parentType' | 'schema'
> & {
  disableControlSelect?: boolean;
  assessmentMode: AssessmentTypeEnum;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
};

const TestResultForm: FC<Props> = ({
  beforeFieldsSlot,
  showAssessmentSelector,
  ...props
}) => {
  const { t } = useTranslation('common');

  const autoPopulateSubmitter = useIsModuleEnabled(
    'control.subModules.control_test_submitter_autopopulate'
  );
  const { user } = useRisksmartUser();

  // When the module is enabled, new results record the logged-in user as
  // Submitter and the field is locked. The lock does not wait for the user
  // profile to resolve, so the field is never briefly editable on first
  // paint. The value is injected into defaultValues/values (rather than set
  // imperatively) so react-hook-form's reactive `values` reset keeps it, and
  // pre-seeded values (e.g. smart copy carries the source result's
  // Submitter) are overridden.
  const lockSubmitter =
    autoPopulateSubmitter && isCreateTestResult(props.values);
  const submitterOverride =
    lockSubmitter && user?.userId
      ? { Submitter: { type: 'user' as const, value: user.userId } }
      : undefined;

  return (
    <CustomisableForm
      {...props}
      defaultValues={{ ...props.defaultValues, ...submitterOverride }}
      values={props.values && { ...props.values, ...submitterOverride }}
      schema={TestResultFormSchema}
      i18n={t('testResults')}
      formId={'test-result-form'}
      parentType={getParentType(props.assessmentMode)}
    >
      {beforeFieldsSlot}
      <TestResultFormFields
        readOnly={props.readOnly}
        disableControlSelect={props.disableControlSelect}
        lockSubmitter={lockSubmitter}
        assessmentMode={props.assessmentMode}
        showAssessmentSelector={showAssessmentSelector}
      />
    </CustomisableForm>
  );
};

export default TestResultForm;
